import { Box, styled } from '@mui/material';
import { MuiDateRangePicker } from 'components/FormElements/DateRangePicker';
import PageFilters, { Filter } from 'components/new/PageFilters';
import Spacing from 'components/Spacing';
import { ActionOperationType } from 'further-types/lp/action';
import { ActionRefLabels } from 'further-ui/labels';
import {
  DateRange,
  getDateRange,
  numberToCurrencyString,
} from 'further-ui/utils';
import { useFilters, usePagination } from 'further-ui/hooks';
import api from 'lib/trpcClient';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import SearchField from 'further-ui/components/SearchField';

const Reconciled = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const PartReconciled = styled(Box)(({ theme }) => ({
  color: theme.palette.warning.main,
}));

type Props = {
  bankAccount: {
    id: string;
    vehicleId: string;
  };
};

const ReconciledActions: React.FC<Props> = ({ bankAccount }) => {
  const pageId = `reconciled-actions-${bankAccount.id}`;

  const pagination = usePagination({
    id: pageId,
    orderBy: 'ledgerDate',
    order: 'desc',
  });

  const { filters, handleChangeFilters, isFiltersChangePending } = useFilters<{
    dateRange?: DateRange;
    searchQuery?: string;
  }>(pageId, {
    defaultFilters: {
      dateRange: undefined,
      searchQuery: '',
    },
    onFiltersChange: pagination.toFirstPage,
  });

  const [data, query] = api.action.listActions.useSuspenseQuery({
    vehicleId: bankAccount.vehicleId,
    reconciledAndPartiallyReconciledOnly: true,
    dateRange: getDateRange(filters.dateRange),
    searchQuery: filters.searchQuery,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    orderBy: 'ledgerDate',
    order: 'desc',
  });

  const columns: Array<TableColumn<(typeof data)['results'][number]>> = [
    {
      key: 'ledgerDate',
      label: 'Date',
      render: (row) => row.ledgerDate.toLabel(),
    },
    {
      key: 'actionRef',
      label: 'Action',
      render: (row) => ActionRefLabels[row.actionRef],
    },
    {
      key: 'description',
      label: 'Description',
    },
    {
      key: 'reference',
      label: 'Reference',
    },
    {
      key: 'spent',
      label: 'Spent',
      render: (row) =>
        numberToCurrencyString(
          row.operationType === ActionOperationType.Spent
            ? row.reconcilableAmountTcy
            : undefined,
          {
            fallback: '-',
            currency: row.currencyCode,
          },
        ),
    },
    {
      key: 'received',
      label: 'Received',
      render: (row) =>
        numberToCurrencyString(
          row.operationType === ActionOperationType.Received
            ? row.reconcilableAmountTcy
            : undefined,
          {
            fallback: '-',
            currency: row.currencyCode,
          },
        ),
    },
    {
      key: 'status',
      label: 'Status',
      render: ({ isFullyReconciled }) =>
        isFullyReconciled ? (
          <Reconciled>Reconciled</Reconciled>
        ) : (
          <PartReconciled>Part-reconciled</PartReconciled>
        ),
    },
  ];

  return (
    <Spacing size="md">
      <PageFilters>
        <Filter size="md">
          <MuiDateRangePicker
            dateRange={filters.dateRange}
            onDateRangeChange={(dateRange) => {
              handleChangeFilters({ dateRange });
            }}
            placeholder="Select date range"
          />
        </Filter>
        <Filter size="md">
          <SearchField
            autoSearchOnDebounce
            name="searchQuery"
            placeholder="Search by description, counterparty or action"
            fullWidth
            value={filters.searchQuery ?? ''}
            onSearch={(searchQuery) => {
              handleChangeFilters({ searchQuery });
            }}
            allowEmptyQuery
            iconPlacement="left"
          />
        </Filter>
      </PageFilters>
      <Table
        disableSorting
        columns={columns}
        rows={data?.results ?? []}
        isLoading={
          query.isFetching ||
          pagination.isPaginationChanging ||
          isFiltersChangePending
        }
        pagination={pagination}
        rowsTotalCount={data?.totalCount ?? 0}
      />
    </Spacing>
  );
};

export default ReconciledActions;
